import {
  RECOMMENDATION_REQUEST_SUCCESS,
  RECOMMENDATION_REQUEST_FAILED,
  RECOMMENDATION_REQUEST_START,
  RECOMMENDATION_CONTENT_FAILED,
  RECOMMENDATION_CONTENT_START,
  RECOMMENDATION_CONTENT_SUCCESS,
  RECOMMENDATION_INDIVIDUAL_REQUEST_FAILED,
  RECOMMENDATION_INDIVIDUAL_REQUEST_SUCCESS,
  RECOMMENDATION_INDIVIDUAL_REQUEST_START,
  CLEAR_RECOMMENDATION,
  RecommendationState,
  RecommendationResponsePayload,
  RecommendationStoreResponse,
  SET_DOB_ERROR,
  ProductConfig,
  RECOMMENDATION_CONFIG_SUCCESS,
  RECOMMENDATION_CONFIG_START,
  RECOMMENDATION_CONFIG_FAILED,
  ProductBenefitConfig,
  ApiErrorDetails
} from './types';
import { reduce, get, toString } from 'lodash';
import {
  convertIndividualRecommendation,
  getRecommendationError,
  mapContent,
  mapRecommendationResponseToPax,
  getRecommendationWithContentError,
  mapProductConfig,
  mapProductBenefitConfig,
  mapApiContent,
  validatePlans
} from './recommendation.utils';
import { ProductContent, RESET_STORE, STORE, WhitelistAction } from '../global';
import { checkWhitelist } from '../reducer-utils';

const initialRecommendationState: RecommendationState = {};

const individualRecommendation = (state, action) => {
  const act = action.payload as RecommendationStoreResponse;
  const validated = act.recommendationData
    ? validatePlans({ recommendationData: act.recommendationData, referenceNumber: toString(act.referenceNumber), loading: false })
    : {};
  return {
    ...state,
    [act.referenceNumber]: { ...act, ...validated }
  };
};
const cases: Record<
  string,
  (
    state: RecommendationState,
    action: {
      type: string;
      payload?: unknown;
    }
  ) => RecommendationState
> = {
  [RECOMMENDATION_REQUEST_START]: (state, action) => {
    const act = action.payload as Array<number>;
    return {
      ...state,
      recommendationFetched: false,
      recommendationError: false,
      recommendationServerError: false,
      apiError: undefined,
      ...reduce(
        act,
        (res, paxRef) => ({
          ...res,
          [paxRef]: {
            loading: true,
            referenceNumber: toString(paxRef)
          }
        }),
        {}
      )
    };
  },
  [RECOMMENDATION_REQUEST_SUCCESS]: (state, action) => {
    const mappedRecommendation = mapRecommendationResponseToPax(action.payload as RecommendationResponsePayload[]);
    const contents = get(state, 'productContents');
    const contentError = getRecommendationWithContentError(mappedRecommendation, contents);
    return {
      ...state,
      recommendationFetched: true,
      recommendationError: getRecommendationError(action.payload as RecommendationResponsePayload[]) || contentError,
      recommendationServerError: false,
      ...mappedRecommendation
    };
  },
  [RECOMMENDATION_REQUEST_FAILED]: (state, action) => {
    const act = action.payload as Array<{ error?: unknown; loading: boolean; referenceNumber: string }>;
    // Extract API error details from the first pax's error if available
    const firstError = get(act, '[0].error');
    const apiErrorData = get(firstError, 'response.data.errors[0]') as ApiErrorDetails | undefined;
    return {
      ...state,
      recommendationFetched: true,
      recommendationError: true,
      recommendationServerError: true,
      apiError: apiErrorData,
      ...convertIndividualRecommendation(act as unknown as RecommendationResponsePayload[])
    };
  },
  [RECOMMENDATION_CONTENT_START]: state => ({
    ...state,
    contentError: false,
    contentFetched: false,
    contentServerError: false
  }),
  [RECOMMENDATION_CONTENT_SUCCESS]: (
    state,
    action: {
      type: string;
      payload?: {
        res: unknown;
        contextPath: string;
      };
    }
  ) => {
    const orgContent = get(action.payload.res, 'organization');
    const contents = mapApiContent(
      action.payload.res as { productConfigs: { plan: { content: ProductContent } }[] } ,
      action.payload.contextPath
    );
    const contentError = getRecommendationWithContentError(state, contents);
    return {
      ...state,
      productContents: contents,
      orgContent,
      contentFetched: true,
      contentError,
      contentServerError: false
    };
  },
  [RECOMMENDATION_CONTENT_FAILED]: (state, action) => ({
    ...state,
    contentError: action.payload,
    contentFetched: false,
    contentServerError: true
  }),
  [RECOMMENDATION_INDIVIDUAL_REQUEST_START]: (state, action) => {
    const act = action.payload as number;
    return {
      ...state,
      [act]: { ...state[act], loading: true }
    };
  },
  [RECOMMENDATION_INDIVIDUAL_REQUEST_SUCCESS]: individualRecommendation,
  [RECOMMENDATION_INDIVIDUAL_REQUEST_FAILED]: individualRecommendation,
  [RECOMMENDATION_CONFIG_START]: state => ({
    ...state,
    configError: false,
    configFetched: false,
    configServerError: false
  }),
  [RECOMMENDATION_CONFIG_SUCCESS]: (state, action) => {
    const orgContent = get(action.payload, 'organizationContract.organization');
    const contents = mapContent(
      action.payload as { organizationContract: { productConfigs: { plan: { content: ProductContent } }[] } }
    );
    const productConfigs = mapProductConfig(
      action.payload as {
        organizationContract: { productConfigs: { plan: { configs: { config: ProductConfig[] } } }[] };
      }
    );

    const productBenefitConfigs = mapProductBenefitConfig(
      action.payload as {
        organizationContract: {
          productConfigs: { plan: { benefits: { configs: { config: ProductBenefitConfig[] } } } }[];
        };
      }
    );
    const contentError = getRecommendationWithContentError(state, contents);
    return {
      ...state,
      productContents: contents,
      productConfigs: { productConfigs, productBenefitConfigs },
      orgContent,
      configFetched: true,
      configError: contentError,
      configServerError: false
    };
  },
  [RECOMMENDATION_CONFIG_FAILED]: (state, action) => ({
    ...state,
    configError: action.payload,
    configFetched: false,
    configServerError: true
  }),
  [CLEAR_RECOMMENDATION]: () => ({}),
  [RESET_STORE]: (state, action) => {
    // Honour resetStore() so the deep-link/agent hand-off flow gets a truly
    // fresh slice instead of leaking stale `recommendationFetched: true` into
    // the next render — which caused QuoteDetails downstream effects to run
    // against reset-cleared `travellersPlan.products` and misbehave.
    const { whitelist } = (action.payload ?? {}) as WhitelistAction;
    return checkWhitelist(whitelist, STORE.RECOMMENDATION, state, initialRecommendationState) as RecommendationState;
  },
  [SET_DOB_ERROR]: (state, action) => {
    const { paxId, error } = action.payload as { paxId: number; error: string };
    return { ...state, dobError: { ...state.dobError, [paxId]: error } };
  }
};

export const recommendation = (
  state: RecommendationState = {},
  action: {
    type: string;
    payload?: unknown;
  } = { type: '', payload: '' }
): RecommendationState => {
  const reducer = cases[action.type];
  return reducer ? reducer(state, action) : state;
};
